import type { SubscriptionTier } from "@/types";
import { normalizeAiModelPreference, type AiModelPreference } from "@/lib/ai/model-preferences";

const TIER_LEVEL: Record<SubscriptionTier, number> = {
  starter: 1,
  builder: 2,
  launcher: 3,
};

/** Validate and sanitize AI model preference based on subscription tier.
 *  Legacy values: sonnet -> regular, opus -> premium. */
export function validateAiModel(
  aiModel: string | null | undefined,
  subscriptionTier: string | null | undefined
): AiModelPreference {
  const requested = normalizeAiModelPreference(aiModel);
  if (requested === "premium") {
    const tier = subscriptionTier as SubscriptionTier | null;
    if (tier && TIER_LEVEL[tier] >= TIER_LEVEL.builder) return "premium";
    return "regular"; // downgrade silently
  }
  return "regular";
}

/** Which tier is required to access each feature group */
const FEATURE_REQUIRED_TIER: Record<string, SubscriptionTier> = {
  // Starter
  plan: "starter",
  brand: "starter",
  // Builder
  private_artifacts: "builder", // S47: make niche/plan/brand private (Builder+)
  // Launcher
  launch_checklist: "launcher",
};

/**
 * Check if the user's tier grants access to a feature.
 * Returns null if allowed, or the required tier name if blocked.
 */
export function checkFeatureAccess(
  userTier: SubscriptionTier | null,
  feature: keyof typeof FEATURE_REQUIRED_TIER,
): SubscriptionTier | null {
  const required = FEATURE_REQUIRED_TIER[feature];
  if (!required) return null; // no gate
  if (!userTier) return required; // no subscription at all
  return TIER_LEVEL[userTier] >= TIER_LEVEL[required] ? null : required;
}

/* ─── Client-safe: step → required tier for UI badges ─── */

export const STEP_REQUIRED_TIER: Record<string, SubscriptionTier> = {
  plan: "starter",
  brand: "starter",
  launch_checklist: "launcher",
};

export const TIER_BADGE: Record<SubscriptionTier, { label: string; className: string }> = {
  starter: { label: "Starter", className: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  builder: { label: "Builder", className: "text-violet-400 bg-violet-500/10 border-violet-500/20" },
  launcher: { label: "Launcher", className: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
};
